import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  List,
  RawButton,
  RedesignNotFoundIcon,
  Responsive,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { FULL_MONTH_DATE_FORMAT } from '@/common/constants';

import { useInvestmentsHoldings } from '../../hooks';
import FundsDetailsModal from '../FundsDetailsModal';
import { useGetInvestorAccountId } from '../hook';
import useInvestmentTabStore, { InvestmentsTab } from '../stores/useInvestmentTab.store';

import FundCard from './FundPreviewCard';
import InvestmentsListWrapper from './PreviewListWrapper';

const MIN_DISPLAYED_ITEMS = 3;

const MAX_DISPLAYED_ITEMS = 7;

interface Props {}
const InvestmentsFunds: React.FC<Props> = () => {
  const { isMobile } = useResponsive();
  const [mobileModalFundId, setMobileModalFundId] = useState<null | string>(null);

  const { t } = useTranslation();
  const setCurrentTab = useInvestmentTabStore((state) => state.setCurrentTab);
  const setHighlightedFund = useInvestmentTabStore((state) => state.setHighlightedFund);
  const accountId = useGetInvestorAccountId();
  const { fundsGroupedByCategories, isHoldingLoading, isSuccessLoaded, lastDateUpdate, fundsList } =
    useInvestmentsHoldings(accountId);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const foundMobileModalItem = useMemo(() => {
    if (mobileModalFundId) {
      return fundsGroupedByCategories.find((item) => item.id === mobileModalFundId);
    }
    return null;
  }, [fundsGroupedByCategories, mobileModalFundId]);
  const formattedList = useMemo(() => {
    if (isExpanded) {
      return [...fundsList].splice(0, MAX_DISPLAYED_ITEMS);
    }
    return [...fundsList].splice(0, MIN_DISPLAYED_ITEMS);
  }, [isExpanded, fundsList]);

  const handleRedirect = useCallback(() => {
    window.scrollTo({ top: 0 });
    setCurrentTab(InvestmentsTab.Performance);
    setHighlightedFund('funds');
  }, [setCurrentTab, setHighlightedFund]);
  const handleRedirectAndOpen = useCallback(
    (id: string) => () => {
      if (isMobile) {
        setMobileModalFundId(id);
      } else {
        window.scrollTo({ top: 0 });
        setCurrentTab(InvestmentsTab.Performance);
        setHighlightedFund(id);
      }
    },
    [isMobile, setCurrentTab, setHighlightedFund]
  );

  const countDifference = useMemo(() => {
    const diff = fundsList.length - MIN_DISPLAYED_ITEMS;
    if (diff <= 0) {
      return 0;
    }
    if (diff > MAX_DISPLAYED_ITEMS) {
      return MAX_DISPLAYED_ITEMS - MIN_DISPLAYED_ITEMS;
    }

    return diff;
  }, [fundsList.length]);

  return (
    <InvestmentsListWrapper
      leftChildren={
        <>
          <Text size={isMobile ? 'xxlarge' : '3xl'} weight={700}>
            {t('Funds')}
          </Text>
          <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
            {isSuccessLoaded && !!formattedList.length && (
              <Box margin={{ top: 's' }}>
                <AppButton onClick={handleRedirect}>{t('View Details')}</AppButton>
              </Box>
            )}
          </Responsive>
        </>
      }
    >
      <List>
        {isHoldingLoading && <List.Preloader testId="activities_preview" count={3} />}
        {!isHoldingLoading && !formattedList.length ? (
          <Box align="center" justify="center" direction="column">
            <RedesignNotFoundIcon color="iconDisabled" size="32px" />
            <Text weight={400} size="medium" margin={{ top: 'xs' }}>
              {t('You don’t have any funds')}
            </Text>
          </Box>
        ) : (
          <>
            {lastDateUpdate && (
              <Text
                weight={400}
                size={isMobile ? 'small' : 'medium'}
                color={isMobile ? 'textBody' : 'textSecondary'}
              >
                {`${t('Date as of')} ${lastDateUpdate.format(FULL_MONTH_DATE_FORMAT)}`}
              </Text>
            )}
            {formattedList.map((item) => (
              <FundCard onClick={handleRedirectAndOpen(item.id)} key={item.id} {...item} />
            ))}
            <FundsDetailsModal item={foundMobileModalItem} onClose={() => setMobileModalFundId(null)} />
            {fundsList.length > MIN_DISPLAYED_ITEMS && MAX_DISPLAYED_ITEMS ? (
              <>
                <RawButton onClick={handleToggleExpand} margin={{ top: 's' }} style={{ lineHeight: 1 }}>
                  <Text weight={400} color="textAccent" style={{ lineHeight: 1 }}>
                    {isExpanded ? t('Show Less') : `${t('Show More')} (${countDifference})`}
                  </Text>
                </RawButton>
                {isExpanded && fundsList.length > MAX_DISPLAYED_ITEMS && (
                  <RawButton onClick={handleRedirect} margin={{ top: 's' }} style={{ lineHeight: 1 }}>
                    <Text weight={400} color="textAccent" style={{ lineHeight: 1 }}>
                      {`${t('Show All Funds')} (${fundsList.length})`}
                    </Text>
                  </RawButton>
                )}
              </>
            ) : null}
          </>
        )}
      </List>
      <Responsive size={Responsive.Mobile}>
        {isSuccessLoaded && !!formattedList.length && (
          <Box margin={{ top: 'spacing24' }}>
            <AppButton fill onClick={handleRedirect}>
              {t('View Details')}
            </AppButton>
          </Box>
        )}
      </Responsive>
    </InvestmentsListWrapper>
  );
};

export default InvestmentsFunds;
